<template>
  <div class="self-hosted">
    <p class="self-hosted__intro mb-2">{{ $t("platform.self_hosted.intro") }}</p>
    <div class="self-hosted__state _warn mb-2">
      <svg class="message-icon mr-1" v-svg symbol="info" size="0 0 17 17" role="info"></svg>
      <p class="mb-0">{{ $t("platform.self_hosted.stats_notice") }}</p>
    </div>

    <div v-if="status && status.auto_sync_active" class="self-hosted__state _ok">
      <svg class="message-icon mr-1" v-svg symbol="check" size="0 0 17 17" role="info"></svg>
      <p class="mb-0">{{ $t("platform.self_hosted.auto_sync_active", { date: status.last_sync_check_at }) }}</p>
    </div>
    <div v-else-if="status && status.outdated" class="self-hosted__state _error">
      <svg class="message-icon mr-1" v-svg symbol="info" size="0 0 17 17" role="info"></svg>
      <div>
        <p class="mb-0">{{ $t("platform.self_hosted.settings_outdated") }}</p>
        <p class="mb-0">{{ $t("platform.self_hosted.update_hint") }}</p>
      </div>
    </div>
    <div v-else-if="status && status.downloaded" class="self-hosted__state">
      <svg class="message-icon mr-1" v-svg symbol="check" size="0 0 17 17" role="info"></svg>
      <p class="mb-0">{{ $t("platform.self_hosted.up_to_date", { date: status.downloaded_at }) }}</p>
    </div>

    <div class="self-hosted__modes mb-3 mt-2">
      <button v-for="tab in tabs" :key="tab" type="button" class="self-hosted__mode" :class="{ active: currentTab === tab }" @click="currentTab = tab">
        {{ $t(`platform.self_hosted.tabs.${tab}`) }}
      </button>
    </div>

    <template v-if="currentTab === 'auto'">
      <p class="self-hosted__text">{{ $t("platform.self_hosted.auto_intro", { minutes: intervalMinutes }) }}</p>

      <div class="self-hosted__step">
        <div class="self-hosted__step-title">1. {{ $t("platform.self_hosted.step_api_key") }}</div>
        <div v-if="apiKeys.length" class="form-item__field">
          <select v-model="apiKeyId" class="self-hosted__select" @change="loadSetup">
            <option :value="null">{{ $t("platform.self_hosted.api_key_placeholder") }}</option>
            <option v-for="key in apiKeys" :key="key.id" :value="key.id">{{ key.name }}</option>
          </select>
        </div>
        <InformationBlock v-else>
          {{ $t("platform.self_hosted.no_api_keys") }}
          <router-link to="/api-keys">{{ $t("platform.self_hosted.create_api_key") }}</router-link>
        </InformationBlock>
      </div>

      <div class="self-hosted__step">
        <div class="self-hosted__step-title">2. {{ $t("platform.self_hosted.step_server") }}</div>
        <div class="self-hosted__variants">
          <button
            v-for="variant in variants"
            :key="variant.key"
            type="button"
            class="self-hosted__variant"
            :class="{ active: currentVariant && currentVariant.key === variant.key }"
            @click="variantKey = variant.key"
          >
            {{ $t(`platform.self_hosted.variants.${variant.key}.title`) }}
          </button>
        </div>
        <template v-if="currentVariant">
          <p class="self-hosted__text mt-2">{{ $t(`platform.self_hosted.variants.${currentVariant.key}.hint`, { file: filename, minutes: intervalMinutes }) }}</p>
          <div v-for="file in currentVariant.files" :key="file.name" class="self-hosted__file">
            <div class="self-hosted__file-head">
              <span class="self-hosted__file-name">{{ file.name }}</span>
              <span>
                <button type="button" class="btn-link" @click="copy(file.content)">{{ $t("copy") }}</button>
                <button type="button" class="btn-link ml-3" @click="saveFile(file.name, file.content)">{{ $t("download") }}</button>
              </span>
            </div>
            <pre class="self-hosted__code">{{ file.content }}</pre>
          </div>
          <div v-if="currentVariant.schedule" class="self-hosted__file">
            <div class="self-hosted__file-head">
              <span class="self-hosted__file-name">crontab -e</span>
              <button type="button" class="btn-link" @click="copy(currentVariant.schedule)">{{ $t("copy") }}</button>
            </div>
            <pre class="self-hosted__code">{{ currentVariant.schedule }}</pre>
          </div>
        </template>
      </div>

      <div class="self-hosted__step">
        <div class="self-hosted__step-title">3. {{ $t("platform.self_hosted.step_embed") }}</div>
        <div class="self-hosted__file">
          <div class="self-hosted__file-head">
            <span class="self-hosted__file-name">HTML</span>
            <button type="button" class="btn-link" @click="copy(embedCode)">{{ $t("copy") }}</button>
          </div>
          <pre class="self-hosted__code">{{ embedCode }}</pre>
        </div>
      </div>
    </template>

    <template v-else>
      <p class="self-hosted__text">{{ $t("platform.self_hosted.manual_intro") }}</p>
      <div class="row align-items-center">
        <div class="col-12 col-md-4 mb-2">
          <button type="button" class="btn _border w-100" :disabled="loading" @click="download">
            <span class="icon-download mr-2">
              <svg v-svg symbol="download" size="0 0 17 17" role="info"></svg>
            </span>
            {{ $t("platform.self_hosted.download") }}
          </button>
        </div>
        <div class="col-12 col-md-8 mb-2">
          <InformationBlock>{{ $t("platform.self_hosted.hint", { file: filename }) }}</InformationBlock>
        </div>
      </div>
      <div class="self-hosted__file">
        <div class="self-hosted__file-head">
          <span class="self-hosted__file-name">HTML</span>
          <button type="button" class="btn-link" @click="copy(embedCode)">{{ $t("copy") }}</button>
        </div>
        <pre class="self-hosted__code">{{ embedCode }}</pre>
      </div>
    </template>
  </div>
</template>
<script>
import subaccsService from "@/api/subaccsService";
import apiService from "@/api/apiService";
import InformationBlock from "@/components/InformationBlock";
import { SELF_HOSTED_SCRIPT_STATUS_EVENT } from "@/helpers/selfHostedScript";

const SCRIPT_TAG = "script";

export default {
  name: "SelfHostedScript",
  components: { InformationBlock },
  props: {
    platformId: {
      type: [Number, String],
      required: true,
    },
    // OnClick "new tab with redirect" reads the publisher's URL from an inline script on the page
    customRedirect: {
      type: Boolean,
      default: false,
    },
    // Enabled sub params as "&p1=sub1&p2=sub2", passed to the script in its URL
    subParams: {
      type: String,
      default: "",
    },
    // API keys and setup are loaded when the block is first shown, the status is needed right away
    active: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tabs: ["auto", "manual"],
      currentTab: "auto",
      status: null,
      setup: null,
      apiKeys: [],
      apiKeyId: null,
      variantKey: "php",
      loading: false,
      setupLoaded: false,
      setupRequest: 0,
    };
  },
  computed: {
    variants() {
      return this.setup ? this.setup.variants : [];
    },
    currentVariant() {
      return this.variants.find((variant) => variant.key === this.variantKey) || this.variants[0] || null;
    },
    intervalMinutes() {
      return this.setup ? this.setup.interval_minutes : 5;
    },
    filename() {
      return (this.setup && this.setup.filename) || `sitelib-${this.platformId}.js`;
    },
    embedCode() {
      const query = this.subParams ? `?${this.subParams.replace(/^&/, "")}` : "";
      let code = `<${SCRIPT_TAG} type="text/javascript" src="/${this.filename}${query}" async></${SCRIPT_TAG}>`;
      if (this.customRedirect) {
        code += `\n<${SCRIPT_TAG}>\n  var userCustomRedirectUrl = "PUT YOUR HTTP[S] URL HERE";\n</${SCRIPT_TAG}>`;
      }
      return code;
    },
  },
  watch: {
    active: {
      immediate: true,
      async handler(active) {
        if (!active || this.setupLoaded) {
          return;
        }
        this.setupLoaded = true;
        const keys = await apiService.getApi();
        this.apiKeys = Array.isArray(keys) ? keys : [];
        this.apiKeyId = this.apiKeys.length ? this.apiKeys[0].id : null;
        this.loadSetup();
      },
    },
  },
  mounted() {
    this.$root.$on(SELF_HOSTED_SCRIPT_STATUS_EVENT, this.onStatusChanged);
    this.loadStatus().then(() => this.$emit("status", this.status));
  },
  beforeDestroy() {
    this.$root.$off(SELF_HOSTED_SCRIPT_STATUS_EVENT, this.onStatusChanged);
  },
  methods: {
    onStatusChanged({ platformId, status }) {
      if (String(platformId) === String(this.platformId)) {
        this.status = status;
      }
    },
    async loadStatus() {
      const res = await subaccsService.getSelfHostedScriptStatus(this.platformId);
      if (res && res.status === 200) {
        this.status = res.data;
      }
    },
    async loadSetup() {
      // A slower answer for a previously chosen key must not overwrite the current one
      const request = ++this.setupRequest;
      const res = await subaccsService.getSelfHostedScriptSetup(this.platformId, this.apiKeyId);
      if (request === this.setupRequest && res && res.status === 200) {
        this.setup = res.data;
      }
    },
    async copy(text) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        } else {
          // No Clipboard API on plain http and in old browsers
          const textarea = document.createElement("textarea");
          textarea.value = text;
          document.body.appendChild(textarea);
          textarea.select();
          const copied = document.execCommand("copy");
          document.body.removeChild(textarea);
          if (!copied) {
            throw new Error("execCommand copy failed");
          }
        }
        this.$alert({ type: "success", title: this.$t("copied_succ") });
      } catch (e) {
        this.$alert({ title: this.$t("sent_error") });
      }
    },
    saveFile(name, content) {
      this.saveBlob(name, new Blob([content], { type: "text/plain" }));
    },
    saveBlob(name, blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async download() {
      this.loading = true;
      const res = await subaccsService.downloadSelfHostedScript(this.platformId);
      this.loading = false;

      if (!res || !res.blob) {
        this.$alert({
          title: this.$t("sent_error"),
          text: res && res.message ? res.message : this.$t("server_error"),
        });
        return;
      }

      this.saveBlob(res.filename, res.blob);
      this.loadStatus();
    },
  },
};
</script>
<style lang="scss" scoped>
.self-hosted {
  &__intro,
  &__text {
    font-size: 14px;
    line-height: 1.4;
    color: #868c9d;
    // Step-by-step hints come from the locales with line breaks
    white-space: pre-line;
  }

  &__state {
    display: flex;
    align-items: baseline;
    padding: 10px 12px;
    border-radius: 6px;
    background: rgba(134, 140, 157, 0.12);
    font-size: 14px;
    line-height: 1.4;

    svg.message-icon {
      flex-shrink: 0;
      width: 12px;
      height: 12px;
    }

    &._ok {
      background: rgba(49, 116, 117, 0.12);
      svg {
        fill: #317475;
      }
    }

    // Same yellow as the Beta badge of the tab
    &._warn {
      background: rgba(241, 205, 77, 0.3);
    }

    &._error {
      background: #c84444;
      color: #fff;
      svg {
        fill: #fff;
      }
    }
  }

  &__modes {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 3px;
    border-radius: 8px;
    border: 1px solid rgba(134, 140, 157, 0.4);
  }

  &__mode {
    padding: 5px 14px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #868c9d;
    font-size: 14px;

    &.active {
      background: rgba(49, 116, 117, 0.12);
      color: inherit;
      font-weight: 600;
    }
  }

  &__step {
    margin-bottom: 16px;
  }

  &__step-title {
    font-weight: 600;
    margin-bottom: 8px;
  }

  &__select {
    width: 100%;
    max-width: 360px;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid rgba(134, 140, 157, 0.4);
    background: transparent;
    color: inherit;
  }

  &__variants {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__variant {
    padding: 6px 14px;
    border-radius: 6px;
    border: 1px solid rgba(134, 140, 157, 0.4);
    background: transparent;
    color: inherit;
    font-size: 14px;

    &.active {
      border-color: #317475;
      background: rgba(49, 116, 117, 0.12);
      font-weight: 600;
    }
  }

  &__file {
    margin-top: 8px;
  }

  &__file-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
  }

  &__file-name {
    font-family: monospace;
    color: #868c9d;
  }

  &__code {
    margin: 4px 0 0;
    padding: 10px 12px;
    max-height: 280px;
    overflow: auto;
    border-radius: 6px;
    background: rgba(134, 140, 157, 0.12);
    font-size: 12px;
    white-space: pre;
  }
}
</style>
